// The hidden legacy payload spans (`mcss-msges_data-cssm`, `mcss-init_data-cssm`)
// carry RAW json_encode output — PHP (page.php:365) and the TS serializers
// (serializers/bootPayload.ts, serializers/subsSpan.ts) both echo the JSON
// straight into the span with no HTML escaping.
//
// Entities inside that JSON are therefore DATA, not markup: the save-side text
// pipeline (PHP clear_html/plain2html, ported in primitives/textPipeline.ts)
// stores body text HTML-escaped, so a quoted sentence reaches the payload as
// `"text":"[&quot;Every day…&quot;]"`. Legacy rendered row text as HTML, so the
// browser decoded those entities at paint; React renders text nodes, so the
// decode has to happen here instead.
//
// It MUST happen after the parse, never before it: decoding the container
// string turns `&quot;` into a bare `"` inside a JSON string value and the
// payload stops being JSON at all (legacy's parseJSON of the mirrored string
// then throws and paging dies).
//
// PURITY: no DOM, no globals — the server render (payloadToState) and the
// client seed must produce byte-identical strings or hydration diverges.

// The standard entities PHP's htmlspecialchars/htmlentities emit.
export function decodeHtmlEntities(text: string): string {
  if (!text.includes('&')) return text
  return text
    .replace(/&#(\d+);/g, (_m, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, code: string) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}

function decodeParsedStrings(value: any): any {
  if (typeof value === 'string') return decodeHtmlEntities(value)
  if (Array.isArray(value)) return value.map(decodeParsedStrings)
  if (value && typeof value === 'object') {
    const next: Record<string, any> = {}
    for (const key of Object.keys(value)) next[key] = decodeParsedStrings(value[key])
    return next
  }
  return value
}

// Parse the raw payload, decode entities in every string VALUE, re-emit valid
// JSON. Both React and legacy consume the result — legacy re-parses the
// mirrored string, so it has to stay parseable.
export function decodePayloadJson(raw: string): string {
  let parsed: any
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Not JSON (or already broken): hand it back untouched rather than
    // corrupting it further. The caller's own parse reports the real error.
    return raw
  }
  return JSON.stringify(decodeParsedStrings(parsed))
}

// Regex on the raw HTML, not a parser: the payload's `html` fields contain
// closing tags that make an HTML parser end the span early.
export function extractPayloadSpanJson(html: string, id: string): string | null {
  const re = new RegExp(`<(?:div|span)[^>]*id=["']${id}["'][^>]*>([\\s\\S]*?)<\\/(?:div|span)>`, 'i')
  const match = html.match(re)
  const text = match?.[1]?.trim()
  if (!text) return null
  return decodePayloadJson(text)
}
